import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { TournamentsAPI, PlayersAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import ScanReveal from "@/components/ScanReveal";
import GoldenParticles from "@/components/GoldenParticles";
import ImmersiveLightbox from "@/components/ImmersiveLightbox";
import { FullPageLoader } from "@/components/Loaders";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { getSport, sportTerms } from "@/lib/sports";
import { themeVars, titleStyles } from "@/lib/appearance";
import ShareButton from "@/components/ShareButton";
import { ArrowLeft, Handshake, Pencil, Trophy, Video } from "lucide-react";
import { isVideoFileUrl, toPlayableVideoUrl, videoPosterUrl } from "@/lib/video";
import { transparentLogoUrl } from "@/lib/logo";
import { LEAGUE_COLUMNS, sortLeagueTeams, leagueSorter } from "@/lib/standings";
import { ROUND_LABELS, ROUND_ORDER, resolveBracket, sourceLabel, finalRanking, overrideRanking } from "@/lib/knockout";
import { orderedSections } from "@/lib/sections";
import ShareStoryButton from "@/components/ShareStoryButton";

const HighlightEmbed = ({ h }) => {
  const url = h.url || "";
  let embedUrl = url;
  if (h.type === "youtube" || /youtube\.com|youtu\.be/.test(url)) {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
    if (m) embedUrl = `https://www.youtube.com/embed/${m[1]}`;
  } else if (h.type === "vimeo" || /vimeo\.com/.test(url)) {
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) embedUrl = `https://player.vimeo.com/video/${m[1]}`;
  }
  const isVideoFile = isVideoFileUrl(url, h.type);
  return (
    <div className="relative aspect-video w-full rounded-xl overflow-hidden border border-white/10 bg-black">
      {isVideoFile ? (
        <video src={toPlayableVideoUrl(url)} controls playsInline preload="metadata" poster={h.poster || videoPosterUrl(url) || undefined} className="w-full h-full object-cover" />
      ) : (
        <iframe
          src={embedUrl}
          title={h.title || "highlight"}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};

const StatBox = ({ label, value, color }) => {
  const v = String(value ?? "");
  const sizeClass =
    v.length >= 8 ? "text-2xl sm:text-3xl md:text-4xl"
    : v.length >= 6 ? "text-3xl sm:text-4xl md:text-5xl"
    : "text-4xl sm:text-5xl md:text-5xl";
  return (
    <div className="bg-[#111216] border border-white/10 rounded-xl p-4 sm:p-5 md:p-6 min-w-0 relative overflow-hidden">
      <div
        className="absolute top-0 left-0 w-1 h-full"
        style={{ background: color }}
      />
      <div className={`font-display ${sizeClass} font-black leading-none whitespace-nowrap text-white`}>
        {v}
      </div>
      <div
        className="font-display font-bold uppercase mt-2 text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.22em] leading-tight text-zinc-500"
      >
        {label}
      </div>
    </div>
  );
};

export default function TournamentProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [tournament, setTournament] = useState(null);
  const [topPlayers, setTopPlayers] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    TournamentsAPI.get(slug)
      .then(async (t) => {
        if (!mounted) return;
        setTournament(t);
        if (t.top_player_ids?.length) {
          const players = await Promise.all(
            t.top_player_ids.map((id) => PlayersAPI.get(id).catch(() => null)),
          );
          if (mounted) setTopPlayers(players.filter(Boolean));
        }
      })
      .catch(() => mounted && setError("Tournoi introuvable"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading)
    return <FullPageLoader caption="Chargement du tournoi…" />;

  if (error || !tournament)
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <div className="max-w-5xl mx-auto w-full px-4 md:px-8 py-24 text-center">
          <div className="font-display text-4xl font-semibold uppercase tracking-tight mb-3">
            Tournoi introuvable
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline"
            data-testid="back-tournaments"
          >
            <ArrowLeft className="w-4 h-4" /> Espace administrateur
          </Link>
        </div>
      </div>
    );

  const accent = tournament.primary_color || "#D4AF37";
  const sportDef = getSport(tournament.sport);
  const ap = tournament.appearance || {};
  const terms = sportTerms(tournament.sport);
  const titleCfg = titleStyles(ap, "xl");
  const isLeague = tournament.standings_format === "league";
  const standingsColumns = isLeague
    ? LEAGUE_COLUMNS
    : sportDef.standings_columns;
  const sortedTeams = [...(tournament.teams || [])].sort(
    isLeague
      ? leagueSorter(tournament.knockout?.tiebreaker)
      : (a, b) => (b.points || 0) - (a.points || 0),
  );
  const koResolved =
    isLeague &&
    tournament.knockout?.matches?.length &&
    (tournament.knockout?.format || "none") !== "none"
      ? resolveBracket(tournament.teams || [], tournament.knockout)
      : [];
  const koById = Object.fromEntries(
    (tournament.knockout?.matches || []).map((m) => [m.id, m]),
  );
  const koByRound = {};
  koResolved.forEach((m) => {
    (koByRound[m.round] ||= []).push(m);
  });
  const koRankingManual = isLeague
    ? overrideRanking(tournament.teams || [], tournament.knockout)
    : [];
  const koRanking = koRankingManual.length
    ? koRankingManual
    : finalRanking(koResolved);
  const secOrder = orderedSections("tournament", tournament.section_order);
  const secStyle = (k) => ({ order: 10 + secOrder.indexOf(k) });

  const groupNames = (() => {
    const seen = [];
    (tournament.teams || []).forEach((t) => {
      const g = (t.group || "").trim();
      if (g && !seen.includes(g)) seen.push(g);
    });
    return seen;
  })();
  const hasGroups = groupNames.length > 0;
  const ungroupedTeams = sortedTeams.filter((t) => !(t.group || "").trim());

  const teamById = Object.fromEntries((tournament.teams || []).map((t) => [t.id, t]));
  const allGroupMatches = (tournament.matches || []).map((m) => ({
    ...m,
    home: teamById[m.home_id],
    away: teamById[m.away_id],
  }));
  const matchesOfGroup = (g) =>
    allGroupMatches.filter(
      (m) =>
        m.home &&
        m.away &&
        (m.home.group || "").trim() === g &&
        (m.away.group || "").trim() === g,
    );
  const groupedMatchIds = new Set(
    groupNames.flatMap((g) => matchesOfGroup(g).map((m) => m.id)),
  );
  const otherMatches = allGroupMatches.filter(
    (m) => m.home && m.away && !groupedMatchIds.has(m.id),
  );

  const MatchLogo = ({ team }) => (
    <div
      className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shrink-0 overflow-hidden border"
      style={{
        backgroundColor: `${team?.primary_color || "#444"}22`,
        borderColor: `${team?.primary_color || "#444"}55`,
      }}
    >
      {team?.logo_url ? (
        <img
          src={transparentLogoUrl(team.logo_url)}
          alt={team.name}
          className="w-full h-full object-contain p-0.5"
        />
      ) : (
        <span
          className="font-display font-black text-xs"
          style={{ color: team?.primary_color || "#fff" }}
        >
          {team?.name?.[0]}
        </span>
      )}
    </div>
  );

  const GroupMatchCard = ({ m }) => {
    const played = m.home_score != null && m.away_score != null;
    return (
      <div
        className="bg-[#111216] border border-white/10 rounded-lg px-3 py-3 grid grid-cols-[1fr,auto,1fr] gap-2 items-center"
        data-testid={`group-match-${m.id}`}
      >
        <div className="flex items-center gap-2 justify-end min-w-0">
          <div className="font-display uppercase tracking-tight font-semibold text-[11px] sm:text-sm text-right leading-tight min-w-0">
            {m.home.name}
          </div>
          <MatchLogo team={m.home} />
        </div>
        <div className="text-center px-1">
          {m.date && (
            <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-body mb-0.5">
              {m.date}
            </div>
          )}
          <div className="font-display font-black text-lg sm:text-xl tabular-nums">
            {played ? (
              <>
                <span
                  style={
                    Number(m.home_score) > Number(m.away_score) ? { color: accent } : {}
                  }
                >
                  {m.home_score}
                </span>
                <span className="text-zinc-600 mx-1">-</span>
                <span
                  style={
                    Number(m.away_score) > Number(m.home_score) ? { color: accent } : {}
                  }
                >
                  {m.away_score}
                </span>
              </>
            ) : (
              <span className="text-zinc-600">vs</span>
            )}
          </div>
          {m.venue && (
            <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-body mt-0.5">
              {m.venue}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <MatchLogo team={m.away} />
          <div className="font-display uppercase tracking-tight font-semibold text-[11px] sm:text-sm leading-tight min-w-0">
            {m.away.name}
          </div>
        </div>
      </div>
    );
  };

  const MatchList = ({ matches }) =>
    matches.length > 0 && (
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        {matches.map((m) => (
          <GroupMatchCard key={m.id} m={m} />
        ))}
      </div>
    );

  const KOTeamRow = ({ team, source, score, pens, isWinner }) => (
    <div className="flex items-center gap-2 min-w-0">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden border"
        style={{
          backgroundColor: `${team?.primary_color || "#444"}22`,
          borderColor: `${team?.primary_color || "#444"}55`,
        }}
      >
        {team?.logo_url ? (
          <img
            src={transparentLogoUrl(team.logo_url)}
            alt={team.name}
            className="w-full h-full object-contain p-0.5"
          />
        ) : (
          <span
            className="font-display font-black text-[9px]"
            style={{ color: team?.primary_color || "#71717A" }}
          >
            {team?.name?.[0] || "?"}
          </span>
        )}
      </div>
      <div
        className={`flex-1 min-w-0 truncate font-display uppercase tracking-tight text-xs sm:text-sm ${
          team
            ? isWinner
              ? "font-black"
              : "font-semibold text-zinc-300"
            : "text-zinc-600 italic normal-case font-body"
        }`}
        style={isWinner ? { color: accent } : {}}
      >
        {team?.name || sourceLabel(source, koById)}
      </div>
      <div
        className={`font-display tabular-nums text-sm sm:text-base ${isWinner ? "font-black" : "text-zinc-400"}`}
        style={isWinner ? { color: accent } : {}}
      >
        {score ?? ""}
        {score != null && score !== "" && pens != null && pens !== "" ? (
          <span className="text-[0.75em] opacity-80">({pens})</span>
        ) : null}
      </div>
    </div>
  );

  const KOMatchCard = ({ m }) => (
    <div
      className="bg-[#111216] border border-white/10 rounded-lg p-3 space-y-2"
      data-testid={`ko-public-${m.label}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
          {m.label}
        </span>
        {(m.date || m.venue) && (
          <span className="text-[9px] text-zinc-600 font-body truncate">
            {[m.date, m.venue].filter(Boolean).join(" · ")}
          </span>
        )}
      </div>
      <KOTeamRow
        team={m.home_team}
        source={m.home_source}
        score={m.home_score}
        pens={m.home_pens}
        isWinner={m.played && m.winner?.id === m.home_team?.id}
      />
      <KOTeamRow
        team={m.away_team}
        source={m.away_source}
        score={m.away_score}
        pens={m.away_pens}
        isWinner={m.played && m.winner?.id === m.away_team?.id}
      />
    </div>
  );

  const StandingsTable = ({ teams, testId }) => (
    <div
      className="bg-[#111216] border border-white/10 rounded-xl overflow-hidden"
      data-testid={testId}
    >
      <div
        className="grid gap-1 sm:gap-2 px-2 sm:px-5 py-3 border-b border-white/10 text-[9px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-zinc-500"
        style={{
          gridTemplateColumns: `28px minmax(0,1fr) ${standingsColumns.map(() => (isLeague ? "26px" : "32px")).join(" ")}`,
        }}
      >
        <div>#</div>
        <div>{terms.entity}</div>
        {standingsColumns.map((c, i) => (
          <div
            key={c.key}
            className="text-right"
            style={
              i === standingsColumns.length - 1
                ? { color: accent }
                : {}
            }
          >
            {c.label}
          </div>
        ))}
      </div>
      {teams.map((team, i) => {
        const linkTo = team.player_id
          ? `/player/${team.player_id}`
          : team.team_id
            ? `/team/${team.team_id}`
            : null;
        const RowTag = linkTo ? Link : "div";
        const rowProps = linkTo ? { to: linkTo } : {};
        return (
          <RowTag
            key={team.id || i}
            {...rowProps}
            className={`grid gap-1 sm:gap-2 px-2 sm:px-5 py-3 sm:py-4 border-b border-white/5 last:border-0 items-center transition-colors ${linkTo ? "hover:bg-white/[0.06] cursor-pointer" : "hover:bg-white/[0.02]"}`}
            style={{
              gridTemplateColumns: `28px minmax(0,1fr) ${standingsColumns.map(() => (isLeague ? "26px" : "32px")).join(" ")}`,
            }}
            data-testid={`team-row-${i}`}
          >
            <div
              className="font-display text-base sm:text-xl font-bold"
              style={{ color: i < 3 ? accent : "#71717A" }}
            >
              {i + 1}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div
                className="w-6 h-6 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden border"
                style={{
                  backgroundColor: `${team.primary_color || "#444"}22`,
                  borderColor: `${team.primary_color || "#444"}55`,
                }}
              >
                {team.logo_url ? (
                  <img
                    src={transparentLogoUrl(team.logo_url)}
                    alt={team.name}
                    className="w-full h-full object-contain p-0.5"
                  />
                ) : (
                  <span
                    className="font-display font-black text-[10px] sm:text-xs"
                    style={{ color: team.primary_color || "#fff" }}
                  >
                    {team.name?.[0]}
                  </span>
                )}
              </div>
              <div className="font-display uppercase tracking-tight font-semibold text-[11px] sm:text-base leading-tight min-w-0 truncate">
                {team.name}
              </div>
            </div>
            {standingsColumns.map((c, idx) => {
              const value = team[c.key] ?? team.extra?.[c.key] ?? 0;
              const isLast =
                idx === standingsColumns.length - 1;
              return (
                <div
                  key={c.key}
                  className={`text-right font-display tabular-nums ${
                    isLast
                      ? "font-black text-base sm:text-lg"
                      : "text-zinc-400"
                  }`}
                  style={isLast ? { color: accent } : {}}
                >
                  {value}
                </div>
              );
            })}
          </RowTag>
        );
      })}
    </div>
  );

  return (
    <ScanReveal slug={tournament.slug} kind="tournament">
    <div className="event-theme min-h-screen bg-[#050505] text-white flex flex-col" style={themeVars(ap)}>
      <SiteHeader playerName={tournament.name} accentColor={accent} />

      {/* Hero */}
      <section className="relative">
        <GoldenParticles count={10} density="subtle" />
        <div className="relative aspect-[16/10] sm:aspect-[16/8] md:aspect-[16/6] w-full overflow-hidden">
          {tournament.cover_image_url ? (
            <img
              src={tournament.cover_image_url}
              alt={tournament.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]" />
          )}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(180deg, rgba(5,5,5,0.2) 30%, rgba(5,5,5,0.95) 100%)`,
            }}
          />
          <div className="absolute top-4 right-4 md:top-8 md:right-8 flex items-center gap-2">
            <ShareButton
              kind="tournament"
              slug={tournament.slug}
              title={tournament.name}
              accent={accent}
              compact
            />
            {(user?.role === "admin" ||
              (user?.role === "organizer" && tournament.organizer_id === user?.id)) && (
              <Link
                to={`/admin/tournaments/${tournament.id}`}
                data-testid="edit-tournament-link"
                className="text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white inline-flex items-center gap-1.5 backdrop-blur-md bg-black/30 border border-white/10 rounded-full px-3 py-1.5"
              >
                <Pencil className="w-3 h-3" /> Éditer
              </Link>
            )}
          </div>
          <div className="absolute left-0 right-0 bottom-0 px-4 md:px-8 pb-8 md:pb-14">
            <div className="max-w-5xl mx-auto">
              {tournament.season && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-overline mb-2"
                  style={{ color: accent }}
                >
                  <Trophy className="w-3 h-3 inline mr-1" /> Saison {tournament.season}
                </motion.div>
              )}
              {!ap.hide_title && (
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.85, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={`font-display ${titleCfg.sizeClass} font-semibold uppercase tracking-tighter leading-[0.85] ${ap.title_color ? "" : "text-gradient-gold"}`}
                  style={{
                    ...(ap.title_color
                      ? { color: ap.title_color }
                      : { filter: "drop-shadow(0 6px 22px rgba(212,175,55,0.22))" }),
                    ...titleCfg.style,
                  }}
                  data-testid="tournament-name"
                >
                  {tournament.name}
                </motion.h1>
              )}
              {tournament.subtitle && (
                <p
                  className="font-body text-base md:text-xl text-zinc-300 mt-3 max-w-2xl"
                  style={ap.subtitle_color ? { color: ap.subtitle_color } : undefined}
                >
                  {tournament.subtitle}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats du tournoi */}
      <section className="max-w-5xl mx-auto w-full px-4 md:px-8 py-12 md:py-16" style={secStyle("stats")}>
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">
            Stats du tournoi
          </h2>
          <span className="text-overline" style={{ color: accent }}>
            {sportDef.label}
            {tournament.season ? ` · Saison ${tournament.season}` : ""}
          </span>
        </div>
        <div
          className={`grid grid-cols-2 gap-3 md:gap-4 ${
            sportDef.tournament_stats.length <= 2
              ? "md:grid-cols-2"
              : sportDef.tournament_stats.length === 3
                ? "md:grid-cols-3"
                : "md:grid-cols-4"
          }`}
        >
          {sportDef.tournament_stats.map((s) => (
            <StatBox
              key={s.key}
              label={s.label}
            value={(() => {
              const manual = tournament.stats?.[s.key];
              if (manual != null && Number(manual) !== 0) return manual;
              if (isLeague && (s.key === "matches" || s.key === "total_matches")) {
                const played =
                  allGroupMatches.filter((m) => m.home_score != null && m.away_score != null)
                    .length + koResolved.filter((m) => m.played).length;
                if (played > 0) return played;
              }
              if (isLeague && (s.key === "goals" || s.key === "total_goals")) {
                const goals =
                  allGroupMatches.reduce(
                    (sum, m) => sum + (Number(m.home_score) || 0) + (Number(m.away_score) || 0),
                    0,
                  ) +
                  koResolved.reduce(
                    (sum, m) =>
                      sum + (m.played ? (Number(m.home_score) || 0) + (Number(m.away_score) || 0) : 0),
                    0,
                  );
                if (goals > 0) return goals;
              }
              return manual ?? 0;
            })()}
              color={accent}
            />
          ))}
        </div>
      </section>

      {/* Classement */}
      <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16" style={secStyle("standings")}>
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">
            {isLeague && hasGroups ? (
              <>
                Phase de <span style={{ color: accent }}>championnat</span>
              </>
            ) : (
              terms.standings
            )}
          </h2>
        </div>
        {hasGroups ? (
          <div className="space-y-10" data-testid="standings-groups">
            {groupNames.map((g) => (
              <div key={g} data-testid={`standings-group-${g}`}>
                <h3
                  className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide mb-3"
                  style={{ color: accent }}
                >
                  {g}
                </h3>
                {isLeague && <MatchList matches={matchesOfGroup(g)} />}
                <StandingsTable
                  teams={sortedTeams.filter((t) => (t.group || "").trim() === g)}
                  testId={`standings-table-${g}`}
                />
              </div>
            ))}
            {ungroupedTeams.length > 0 && (
              <div data-testid="standings-group-others">
                <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide mb-3 text-zinc-400">
                  Hors groupe
                </h3>
                {isLeague && <MatchList matches={otherMatches} />}
                <StandingsTable teams={ungroupedTeams} testId="standings-table-others" />
              </div>
            )}
            {ungroupedTeams.length === 0 && isLeague && otherMatches.length > 0 && (
              <div data-testid="other-matches">
                <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide mb-3 text-zinc-400">
                  Autres matchs
                </h3>
                <MatchList matches={otherMatches} />
              </div>
            )}
          </div>
        ) : (
          <>
            {isLeague && <MatchList matches={allGroupMatches.filter((m) => m.home && m.away)} />}
            <StandingsTable teams={sortedTeams} testId="standings-table" />
          </>
        )}
      </section>

      {koResolved.length > 0 && (
        <section
          className="max-w-6xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
          data-testid="knockout-section"
          style={secStyle("knockout")}
        >
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6">
            Phase à <span style={{ color: accent }}>éliminations</span>
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0 snap-x">
            {["r16", "qf", "sf", "final"]
              .filter((r) => koByRound[r]?.length)
              .map((round) => (
                <div
                  key={round}
                  className="flex flex-col gap-3 min-w-[250px] sm:min-w-[280px] flex-1 snap-start"
                >
                  <div
                    className="text-[10px] uppercase tracking-[0.22em] font-bold"
                    style={{ color: accent }}
                  >
                    {ROUND_LABELS[round]}
                  </div>
                  <div className="flex flex-col gap-3 justify-around flex-1">
                    {koByRound[round].map((m) => (
                      <KOMatchCard key={m.id} m={m} />
                    ))}
                    {round === "final" &&
                      koByRound.third?.map((m) => (
                        <KOMatchCard key={m.id} m={m} />
                      ))}
                  </div>
                </div>
              ))}
          </div>

          {(koByRound.c_sf || koByRound.c5 || koByRound.c7) && (
            <div className="mt-8">
              <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide mb-4 text-zinc-400">
                Matchs de classement
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {["c_sf", "c5", "c7"]
                  .flatMap((r) => koByRound[r] || [])
                  .map((m) => (
                    <KOMatchCard key={m.id} m={m} />
                  ))}
              </div>
            </div>
          )}

        </section>
      )}

      {koRanking.length > 0 && (
        <section
          className="max-w-6xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
          data-testid="final-ranking-section"
          style={secStyle("final_ranking")}
        >
            <div data-testid="final-ranking">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h3 className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide">
                  Classement <span style={{ color: accent }}>final</span>
                </h3>
                <ShareStoryButton
                  filename={`classement-${tournament.slug}`}
                  card={{
                    title: tournament.name,
                    subtitle: tournament.subtitle,
                    kicker: "Classement final",
                    accent,
                    rows: koRanking.map((r) => ({
                      position: r.position,
                      name: r.team?.name,
                      logo: r.team?.logo_url,
                      color: r.team?.primary_color,
                    })),
                    footer: `${window.location.host}/tournament/${tournament.slug}`,
                  }}
                />
              </div>
              <div className="bg-[#111216] border border-white/10 rounded-xl overflow-hidden">
                {koRanking.map((r) => (
                  <div
                    key={r.position}
                    className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-white/5 last:border-0"
                    data-testid={`final-rank-${r.position}`}
                  >
                    <div
                      className="font-display text-lg sm:text-xl font-black w-8"
                      style={{ color: r.position <= 3 ? accent : "#71717A" }}
                    >
                      {r.position}
                      <span className="text-[10px] align-top">{r.position === 1 ? "er" : "e"}</span>
                    </div>
                    <div
                      className="w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden border"
                      style={{
                        backgroundColor: `${r.team.primary_color || "#444"}22`,
                        borderColor: `${r.team.primary_color || "#444"}55`,
                      }}
                    >
                      {r.team.logo_url ? (
                        <img
                          src={transparentLogoUrl(r.team.logo_url)}
                          alt={r.team.name}
                          className="w-full h-full object-contain p-0.5"
                        />
                      ) : (
                        <span
                          className="font-display font-black text-[10px]"
                          style={{ color: r.team.primary_color || "#fff" }}
                        >
                          {r.team.name?.[0]}
                        </span>
                      )}
                    </div>
                    <div className="font-display uppercase tracking-tight font-semibold text-sm sm:text-base">
                      {r.team.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
        </section>
      )}

      {/* Highlights vidéos du tournoi */}
      {settings?.show_tournament_videos !== false && tournament.highlights?.length > 0 && (
        <section
          className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
          data-testid="tournament-highlights"
          style={secStyle("videos")}
        >
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">
              Vidéos de l&apos;évènement
            </h2>
            <span className="text-overline" style={{ color: accent }}>
              <Video className="w-3 h-3 inline mr-1" />
              {tournament.highlights.length} vidéo{tournament.highlights.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {tournament.highlights.map((h) => (
              <div key={h.id} data-testid={`tournament-highlight-${h.id}`}>
                <HighlightEmbed h={h} />
                {h.title && (
                  <div className="mt-3 font-display uppercase tracking-wider text-zinc-300">
                    {h.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Top players */}
      {(topPlayers.length > 0 ||
        (tournament.top_players_custom || []).filter((p) => (p.name || "").trim()).length > 0) && (
        <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16" style={secStyle("top_players")}>
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6">
            {terms.individual ? `Meilleurs ${terms.countLabel}` : "Meilleurs joueurs"}
          </h2>
          <div className="space-y-2">
            {topPlayers.map((p, i) => (
              <Link
                key={p.id}
                to={`/player/${p.slug}`}
                data-testid={`top-player-${p.slug}`}
                className="grid grid-cols-[50px,60px,1fr,auto] gap-3 sm:gap-4 items-center bg-[#111216] border border-white/10 rounded-xl p-3 sm:p-4 hover:border-[#0066FF]/60 transition-colors"
              >
                <div className="font-display text-2xl sm:text-3xl font-black" style={{ color: accent }}>
                  {i + 1}
                </div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border border-white/10 shrink-0">
                  {p.hero_image_url ? (
                    <img src={p.hero_image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center font-display text-zinc-600">
                      {p.name?.[0]}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg sm:text-xl font-bold uppercase truncate">{p.name}</div>
                  <div className="text-overline text-zinc-400 truncate">
                    {p.club_name || p.position}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black" style={{ color: accent }}>
                    {p.stats?.goals ?? 0}
                  </div>
                  <div className="text-overline text-zinc-500">Buts</div>
                </div>
              </Link>
            ))}
            {(tournament.top_players_custom || [])
              .filter((p) => (p.name || "").trim())
              .map((p, i) => (
                <div
                  key={p.id || i}
                  data-testid={`top-player-custom-${i}`}
                  className="grid grid-cols-[50px,60px,1fr,auto] gap-3 sm:gap-4 items-center bg-[#111216] border border-white/10 rounded-xl p-3 sm:p-4"
                >
                  <div className="font-display text-2xl sm:text-3xl font-black" style={{ color: accent }}>
                    {topPlayers.length + i + 1}
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border border-white/10 shrink-0">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center font-display text-zinc-600">
                        {p.name?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg sm:text-xl font-bold uppercase truncate">{p.name}</div>
                    {p.club && <div className="text-overline text-zinc-400 truncate">{p.club}</div>}
                  </div>
                  <div className="text-right">
                    {p.stat_value && (
                      <>
                        <div className="font-display text-2xl font-black" style={{ color: accent }}>
                          {p.stat_value}
                        </div>
                        <div className="text-overline text-zinc-500">Buts</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Récompenses d'équipe */}
      {(tournament.team_awards || []).filter((a) => (a.title || "").trim() || (a.team_name || "").trim()).length > 0 && (
        <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16" data-testid="tournament-team-awards" style={secStyle("team_awards")}>
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6">
            Récompenses d'équipe
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {tournament.team_awards
              .filter((a) => (a.title || "").trim() || (a.team_name || "").trim())
              .map((a, i) => (
                <div
                  key={a.id || i}
                  className="relative overflow-hidden bg-[#111216] border border-white/10 rounded-xl p-3.5 flex items-center gap-3.5"
                  data-testid={`tournament-team-award-${i}`}
                >
                  <div
                    className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-20 pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
                  />
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center border shrink-0 overflow-hidden"
                    style={{ background: `${accent}12`, borderColor: `${accent}45` }}
                  >
                    {a.photo_url ? (
                      <img
                        src={transparentLogoUrl(a.photo_url)}
                        alt={a.team_name}
                        className="w-full h-full object-contain p-1.5"
                      />
                    ) : (
                      <Handshake className="w-5 h-5" style={{ color: accent }} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>
                      {a.title}
                    </div>
                    <div className="font-display text-lg font-black uppercase tracking-tight leading-tight truncate">
                      {a.team_name}
                    </div>
                    {a.note && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-0.5 truncate">{a.note}</div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Photos */}
      {(tournament.gallery || []).length > 0 && (
        <section
          className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
          data-testid="tournament-gallery"
          style={secStyle("gallery")}
        >
          <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6">
            Photos
          </h2>
          <div className="hidden md:grid grid-cols-3 gap-4">
            {tournament.gallery.map((g, i) => (
              <motion.button
                type="button"
                key={g.id}
                onClick={() => setLightboxIndex(i)}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.04 }}
                className="aspect-square rounded-2xl overflow-hidden border border-white/10 group relative shine-on-hover focus:outline-none focus:ring-2 focus:ring-[color:var(--gold)]"
                data-testid={`tournament-gallery-${g.id}`}
              >
                <img
                  src={g.url}
                  alt={g.caption || ""}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                />
              </motion.button>
            ))}
          </div>
          <div className="md:hidden flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-4 px-4">
            {tournament.gallery.map((g, i) => (
              <button
                type="button"
                key={g.id}
                onClick={() => setLightboxIndex(i)}
                className="snap-start shrink-0 w-[78%] aspect-square rounded-2xl overflow-hidden border border-white/10"
                data-testid={`tournament-gallery-mobile-${g.id}`}
              >
                <img src={g.url} alt={g.caption || ""} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </section>
      )}
      <ImmersiveLightbox
        items={tournament.gallery || []}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        testId="tournament-gallery-lightbox"
      />

      {/* Partenaires */}
      {(tournament.sponsors || []).length > 0 && (
        <section
          className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
          data-testid="tournament-sponsors"
          style={secStyle("sponsors")}
        >
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">
              Partenaires
            </h2>
            <span className="text-overline" style={{ color: accent }}>
              <Handshake className="w-3 h-3 inline mr-1" /> {tournament.sponsors.length}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {tournament.sponsors.map((s, i) => {
              const Tag = s.website ? "a" : "div";
              const props = s.website
                ? { href: s.website, target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
                <Tag
                  key={s.id || i}
                  {...props}
                  className={`flex flex-col items-center justify-center gap-3 bg-[#111216] border border-white/10 rounded-xl p-5 min-h-[120px] ${s.website ? "hover:border-[#D4AF37]/60 transition-colors" : ""}`}
                  data-testid={`tournament-sponsor-${i}`}
                >
                  {s.logo_url ? (
                    <img
                      src={transparentLogoUrl(s.logo_url)}
                      alt={s.name}
                      className="max-h-14 max-w-[80%] object-contain"
                      style={transparentLogoUrl(s.logo_url) === s.logo_url ? { mixBlendMode: "screen" } : undefined}
                    />
                  ) : (
                    <Handshake className="w-8 h-8 text-zinc-600" />
                  )}
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold text-center">
                    {s.name}
                  </div>
                </Tag>
              );
            })}
          </div>
        </section>
      )}

      {/* QR + Logo — retiré de la page publique (réservé à l'administration) */}

      <footer className="border-t border-white/5 py-8 text-center" style={{ order: 99 }}>
        {user && (
          <Link
            to="/tournaments"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body"
          >
            <ArrowLeft className="w-3 h-3" /> Tous les tournois
          </Link>
        )}
        <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-700">
          Laser Tech · Création · Genève
        </div>
      </footer>
    </div>
    </ScanReveal>
  );
}
